import type { ReactNode } from 'react';
import { cn } from '@herit-fe/heritage-ui';

/**
 * 영역(카드) 프리미티브 — 제목 + 본문 영역. 본문은 muted 톤이지만 **블록(표·목록·폼)을 담을 수 있게**
 * `div` 로 렌더한다(`p` 안에 표/블록을 넣으면 무효 DOM). placeholder 설명 텍스트 단계부터 실데이터
 * 목록 카드까지 공용 — 목록 본문은 `DataListSection` 과 조합.
 *
 * `action` = 제목 우측 액션 슬롯(선택) — 목록 카드의 [등록] 등. 페이지 헤더(h1)가 아니라 본문 카드 헤더에
 * 둬야 논리적(필터/초기화 아래, 목록 위). 미지정 시 제목만(기존 동작 유지).
 * `titleInfo` = 제목 바로 옆(우) 보조 슬롯(선택) — 예: 개인정보 마스킹 안내 (i) 툴팁.
 * `leading` = 제목 **왼쪽** 슬롯(선택) — 예: 지도 드릴 뒤로 버튼(아이콘, breadcrumb "← 지역명"). 미지정 시 없음.
 * `className` = 외곽 section 병합 클래스(선택) — 예: `ELEVATED_SURFACE`(테두리 제거 + soft 그림자). `Card` 정합.
 */
export function RegionCard({
  title,
  titleInfo,
  leading,
  action,
  className,
  children,
}: {
  title: string;
  titleInfo?: ReactNode;
  leading?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    // flex-col + 본문 flex-1 = grid stretch 로 늘어난 높이를 본문이 받아 채운다(옆 카드 등높이일 때 내부 h-full
    // 이 해석됨). stretch 되지 않은 카드는 본문이 content 높이 그대로라 무변(하위호환).
    <section className={cn('flex flex-col rounded-xl border border-border bg-card p-4', className)}>
      {/* 헤더 — 좁은 폭에서 타이틀이 액션과 경쟁해 글자 단위로 줄바꿈되던 문제: 모바일 세로 스택(타이틀 한 줄
          full-width) → sm↑ 가로. 타이틀은 shrink-0·nowrap, 액션이 줄바꿈/축소를 흡수. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex shrink-0 items-center gap-1.5">
          {leading}
          <h2 className="text-sm font-semibold whitespace-nowrap text-foreground">{title}</h2>
          {titleInfo}
        </div>
        {action}
      </div>
      <div className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}
